/**
 * Casca do painel: cabecalho, barra de estado e a montagem dos paineis.
 *
 * Responsabilidade: montar os paineis uma vez, decidir quais ficam na home e
 * quais ficam na folha, redesenhar todos quando o `store` mudar, e — o mais
 * importante desta fase — dizer a verdade sobre a idade do que esta na tela.
 *
 * NAO e responsabilidade deste arquivo: buscar dado, manter socket, ou mutar
 * qualquer coisa alem do proprio DOM.
 *
 * ## O que fica na home
 *
 * O que se OLHA: o trilho de aparelhos, as leituras, o grafico, as
 * saidas e o timelapse. Ajustes e comandos sairam daqui para a folha
 * (`views/folha.ts`) porque sao o que se MEXE de vez em quando e o que se
 * consulta quando algo deu errado — os dois juntos empurravam o timelapse para
 * baixo da dobra em qualquer celular, numa tela cujo trabalho principal e ser
 * olhada de relance.
 *
 * Todos continuam na mesma lista `paineis` e recebem `atualizar()` no mesmo
 * ciclo, estejam na home ou atras da folha: um painel que so recalcula quando
 * esta visivel abriria mostrando o estado de quando foi fechado.
 *
 * ## A barra de estado e o gate da C9
 *
 * O gate desta fase e: em modo aviao, o app abre com dado velho e um indicador
 * HONESTO de desconectado. Honesto quer dizer tres coisas, e as tres estao
 * nesta barra:
 *
 * 1. **Que esta desconectado** — sem pintar "ao vivo" enquanto o socket morreu.
 * 2. **De quando e o dado** — hora absoluta E idade relativa, porque "há 3 h"
 *    sozinho nao diz se o cultivo passou a noite sem ninguem olhando.
 * 3. **Que o controlador nao depende disto** — a regra §0 do contrato. Alguem
 *    que abre o painel remoto no aeroporto e ve "sem conexao" precisa saber, na
 *    mesma tela, que o exaustor continua ligando sozinho.
 *
 * Por isso a barra nao e um icone: e uma frase. Um ponto vermelho no canto
 * seria facil de ignorar, e a informacao mais importante que esta pagina pode
 * dar e justamente que ela nao esta atualizada.
 *
 * ## Redesenho periodico
 *
 * "há 4 min" envelhece sozinho. Sem o relogio abaixo, uma aba parada mostraria
 * "agora" indefinidamente — que e a mentira mais facil de contar sem querer.
 */

import * as acoes from "./../acoes";
import { iconeUi } from "./../icons";
import * as store from "./../store";
import { el, haQuanto, relogio, type Painel } from "./../ui";
import * as ajustes from "./ajustes";
import * as alertas from "./alertas";
import * as aparelhos from "./aparelhos";
import * as comandos from "./comandos";
import * as conta from "./conta";
import * as firmware from "./firmware";
import * as folha from "./folha";
import * as grafico from "./grafico";
import * as leituras from "./leituras";
import * as saidas from "./saidas";
import * as timelapse from "./timelapse";

const RELOGIO_MS = 15_000;

export function montar(aoSair: () => void): { raiz: HTMLElement; destruir: () => void } {
  const naHome = {
    aparelhos: aparelhos.montar(),
    leituras: leituras.montar(),
    grafico: grafico.montar(),
    saidas: saidas.montar(),
    timelapse: timelapse.montar(),
  };
  const naFolha = {
    ...ajustes.montar(),
    conta: conta.montar(() => {
      configuracao.fechar();
      aoSair();
    }),
    firmware: firmware.montar(),
    comandos: comandos.montar(),
    alertas: alertas.montar(),
  };
  const paineis: Painel[] = [...Object.values(naHome), ...Object.values(naFolha)];

  const configuracao = folha.montar([
    {
      id: "config",
      rotulo: "Configuração",
      // Mesma ordem do painel local (`gfc/firmware/data/index.html`):
      // Automacao, Fotoperiodo, Estagio da Planta, VPD, Camera. Seguranca vai
      // por ultimo porque nao existe secao equivalente la.
      conteudo: [
        naFolha.automacao.raiz,
        naFolha.fotoperiodo.raiz,
        naFolha.estagio.raiz,
        naFolha.vpd.raiz,
        naFolha.camera.raiz,
        naFolha.seguranca.raiz,
        naFolha.notificacoes.raiz,
      ],
    },
    {
      id: "alertas",
      rotulo: "Alertas",
      // Aba propria, e nao um painel na home: o feed e histórico, e a home e o
      // que se OLHA de relance. Quem precisa dele ou foi trazido por uma
      // notificacao, ou viu o badge no cabecalho — e nos dois casos vem
      // procurando, nao passando o olho.
      //
      // E aba propria, e nao um cartao dentro de "Sistema", porque o alerta e
      // do TENANT: ele fala de todos os aparelhos, enquanto tudo em Sistema
      // fala do que esta selecionado no trilho.
      conteudo: [naFolha.alertas.raiz],
    },
    {
      id: "sistema",
      rotulo: "Sistema",
      // Firmware antes do feed de comandos: e uma acao, e o feed e o registro
      // do que ela produziu — inclusive o ack "vou baixar" dela.
      conteudo: [naFolha.conta.raiz, naFolha.firmware.raiz, naFolha.comandos.raiz],
    },
  ],
  // Ler o feed E a acao de ler: um segundo clique em "marcar como lido" seria
  // burocracia. Fica aqui, e nao no `onclick` do sino, porque ha tres caminhos
  // ate esta aba — o sino, o botao de configuracao e reabrir a folha na ultima
  // aba — e pendurar a marcacao num deles deixava os outros dois sem marcar.
  (aba) => {
    if (aba === "alertas") void acoes.marcarAlertasLidos();
  });

  const titulo = el("div", { class: "title", text: "GrowController" });

  // O sino, com a contagem de nao-lidos. Abre a aba de alertas E marca como
  // lido: ler o feed E a acao de ler, e um segundo clique em "marcar" seria
  // burocracia. O corte vai ate o alerta mais novo JA desenhado, entao um
  // alarme que chegue no meio do caminho continua contando (ver `acoes.ts`).
  const contador = el("span", { class: "badge", hidden: true });
  const botaoAlertas = el(
    "button",
    {
      type: "button",
      class: "icon-btn sino",
      id: "abrir-alertas",
      "aria-label": "Alertas",
      // Marcar como lido nao acontece aqui: e o `aoMostrar` da folha que cuida
      // disso, para valer tambem quando a aba e alcancada por outro caminho.
      onclick: () => configuracao.abrir("alertas"),
    },
    el("span", { class: "sino-glifo", text: "🔔", "aria-hidden": "true" }),
    contador,
  );
  const pontoStatus = el("span", { class: "status-dot" });
  const textoStatus = el("span", { class: "status-text", text: "conectando…" });

  const cabecalho = el(
    "header",
    {},
    el(
      "div",
      { class: "brand" },
      el("span", { class: "leaf", text: "🌱" }),
      el(
        "div",
        {},
        titulo,
        el("div", { class: "status" }, pontoStatus, textoStatus),
      ),
    ),
    el(
      "div",
      { class: "cabecalho-acoes" },
      botaoAlertas,
      el(
        "button",
        {
          type: "button",
          class: "icon-btn",
          id: "abrir-folha",
          "aria-label": "Abrir configurações",
          onclick: () => configuracao.abrir(),
        },
        iconeUi("mais"),
      ),
    ),
  );

  const barra = el("div", { class: "barra-estado", id: "barra-estado", hidden: true });
  const aviso = el("div", { class: "aviso", id: "aviso", hidden: true });

  // O trilho e as leituras atravessam a largura toda; os paineis de baixo vao
  // para um container proprio porque em tela larga eles se empacotam em duas
  // colunas (ver `.paineis` no CSS) e cada um tem altura propria — em grid, os
  // dois de uma mesma linha ficariam presos a altura do mais alto, com um
  // buraco embaixo do mais baixo.
  // O grafico fica junto das leituras, e fora do fluxo de duas colunas: ele e
  // o detalhe do cartao que acabou de ser tocado, e mandar essa relacao para a
  // coluna da direita em tela larga a esconderia.
  const raiz = el(
    "main",
    { class: "app" },
    cabecalho,
    barra,
    aviso,
    naHome.aparelhos.raiz,
    naHome.leituras.raiz,
    naHome.grafico.raiz,
    el("div", { class: "paineis" }, naHome.saidas.raiz, naHome.timelapse.raiz),
    configuracao.raiz,
  );

  function atualizar(): void {
    const estado = store.obter();

    titulo.textContent = estado.sessao?.tenant_name ?? "GrowController";

    const aoVivo = estado.conexao === "ao-vivo";
    pontoStatus.className = `status-dot ${aoVivo ? "online" : estado.conexao === "sem-rede" ? "alarm" : ""}`;
    textoStatus.textContent = statusCurto(estado);

    raiz.classList.toggle("velho", !aoVivo && estado.aparelhos.length > 0);
    desenharBarra(barra, estado);

    aviso.hidden = estado.aviso === null;
    aviso.textContent = estado.aviso ?? "";

    // "9+" e nao o numero exato: acima de nove o que importa e "muita coisa
    // aconteceu", e tres digitos nao caberiam no ponto sem empurrar o
    // cabecalho.
    contador.hidden = estado.naoLidos === 0;
    contador.textContent = estado.naoLidos > 9 ? "9+" : String(estado.naoLidos);
    botaoAlertas.setAttribute(
      "aria-label",
      estado.naoLidos === 0 ? "Alertas" : `Alertas — ${estado.naoLidos} não lidos`,
    );

    for (const painel of paineis) painel.atualizar();
  }

  const desassinar = store.assinar(atualizar);
  const relogioId = window.setInterval(atualizar, RELOGIO_MS);
  atualizar();

  return {
    raiz,
    destruir: () => {
      desassinar();
      clearInterval(relogioId);
    },
  };
}

function statusCurto(estado: Readonly<store.Estado>): string {
  switch (estado.conexao) {
    case "ao-vivo":
      return estado.latenciaMs === null ? "ao vivo" : `ao vivo · ${estado.latenciaMs} ms`;
    case "conectando":
      return "conectando…";
    case "sem-rede":
      return "sem conexão";
    default:
      return "…";
  }
}

function desenharBarra(barra: HTMLElement, estado: Readonly<store.Estado>): void {
  if (estado.conexao === "ao-vivo") {
    barra.hidden = true;
    return;
  }
  barra.hidden = false;
  barra.className = `barra-estado ${estado.conexao === "sem-rede" ? "ruim" : "aguardando"}`;

  const idade =
    estado.atualizadoEm === null
      ? null
      : `de ${relogio(estado.atualizadoEm)} (${haQuanto(estado.atualizadoEm)})`;

  const primeira =
    estado.conexao === "sem-rede"
      ? idade === null
        ? "Sem conexão com a nuvem, e nada guardado deste painel ainda."
        : `Sem conexão com a nuvem. Na tela, o último dado recebido — ${idade}.`
      : idade === null
        ? "Conectando…"
        : `Conectando… na tela, o último dado recebido — ${idade}.`;

  barra.replaceChildren(
    el("p", { class: "barra-linha", text: primeira }),
    el("p", {
      class: "barra-linha fraca",
      text:
        "O controlador não depende desta página: automação, log no cartão e o " +
        "painel local em grow.local seguem funcionando.",
    }),
  );
}
